import React, { useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  maxBreakpointQuery,
  standardColours,
  brandColours,
  fontSize,
  zIndexLayers,
  standardTransition,
  breakpointSizes,
} from '../styles';
import { Link } from './ui';
import MegaMenu from './MegaMenu';

const StyledNavigation = styled.nav``;

const StyledToggle = styled.button`
  ${maxBreakpointQuery.mlarge`
    position: relative;
    margin: 0;
    padding: 32px 29px;
    height: 100%;
    background-color: ${brandColours.primary};
  `}

  ${maxBreakpointQuery.tsmall`
    padding: 27px 24px;
  `}

  ${minBreakpointQuery.mlarge`
    display: none;
  `}

  span {
    ${maxBreakpointQuery.mlarge`
      display: block;
      margin-top: 7px;
      margin-bottom: 7px;
      height: 2px;
      width: 26px;
      background-color: ${standardColours.white};
      border-radius: 1px;
    `}

    ${maxBreakpointQuery.tsmall`
      margin-top: 5.5px;
      margin-bottom: 5.5px;
      height: 1px;
      width: 20px;
    `}

    &:first-child {
      ${maxBreakpointQuery.mlarge`
        margin-top: 0;
      `}
    }

    &:last-child {
      ${maxBreakpointQuery.mlarge`
        margin-bottom: 0;
      `}
    }

    ${({ isNavActive }) => {
      if (isNavActive) {
        return css`
          ${maxBreakpointQuery.mlarge`
            opacity: 0;
          `}

          &:first-child,
          &:last-child {
            ${maxBreakpointQuery.mlarge`
              opacity: 1;
            `}
          }

          &:first-child {
            ${maxBreakpointQuery.mlarge`
              transform: translateY(9px) rotate(45deg);
            `}

            ${maxBreakpointQuery.tsmall`
              transform: translateY(6.5px) rotate(45deg);
            `}
          }

          &:last-child {
            ${maxBreakpointQuery.mlarge`
              transform: translateY(-9px) rotate(-45deg);
            `}

            ${maxBreakpointQuery.tsmall`
              transform: translateY(-6.5px) rotate(-45deg);
            `}
          }
        `;
      }
    }}
  }
`;

const StyledList = styled.ul`
  ${maxBreakpointQuery.mlarge`
    position: absolute;
    right: 0;
    left: 0;
    overflow: auto;
    z-index: ${zIndexLayers.fourth};
    height: 100%;
    padding: 10px 0;
    color: ${brandColours.primary};
    background-color: ${standardColours.white};
    border-top: solid 1px ${brandColours.primary};
  `}

  ${({ isNavActive }) => {
    if (!isNavActive) {
      return css`
        ${maxBreakpointQuery.mlarge`
          display: none;
        `}
      `;
    }
  }}
`;

// moved up for hover
const StyledLink = styled(Link)`
  position: relative;
  cursor: pointer;

  ${maxBreakpointQuery.mlarge`
    display: block;
    padding: 8px 50px 8px 20px;
    ${fontSize(20)};
  `}

  ${maxBreakpointQuery.tsmall`
    ${fontSize(18)};
  `}

  ${minBreakpointQuery.mlarge`
    padding: 8px 3px;
    color: ${({ alt }) => (alt ? standardColours.white : 'inherit')};
    ${fontSize(14)};
    border-radius: 10px;
    transition: ${standardTransition('color')}, ${standardTransition('background-color')};
  `}

  ${minBreakpointQuery.large`
    padding-right: 5px;
    padding-left: 5px;
    ${fontSize(15)};
  `}

  ${minBreakpointQuery.xxlarge`
    padding-right: 20px;
    padding-left: 8px;
  `}

  ${minBreakpointQuery.xxxlarge`
    ${fontSize(16)};
  `}

  ${minBreakpointQuery.xxxxlarge`
    padding-right: 26px;
    padding-left: 10px;
  `}

  > span {
    position: relative;
  }

  &:after {
    ${maxBreakpointQuery.mlarge`
      content: '';
      position: absolute;
      top: 50%;
      right: 20px;
      transform: translateY(-50%) rotate(45deg);
      border: solid ${brandColours.primary};
      border-width: 0 2px 2px 0;
      height: 8px;
      width: 8px;
    `}

    ${minBreakpointQuery.xxlarge`
      content: '';
      position: absolute;
      top: 50%;
      right: 8px;
      height: 7px;
      width: 7px;
      border: 2px solid ${({ alt, altTheme }) =>
        alt || altTheme ? standardColours.white : brandColours.primary};
      border-top: none;
      border-left: none;
      border-radius: 1px;
      transform: translateY(-50%) rotate(45deg);
      transition: ${standardTransition('border-color')};
    `}

    ${minBreakpointQuery.xxxxlarge`
      right: 10px;
    `}
  }

  &.current-page {
    ${minBreakpointQuery.mlarge`
      color: ${standardColours.white};
      background-color: ${brandColours.primary};
    `}

    &:after {
      ${minBreakpointQuery.mlarge`
        border-color: ${standardColours.white};
      `}
    }
  }
`;

// moved up for hover
const StyledMegaMenu = styled(MegaMenu)`
  ${minBreakpointQuery.mlarge`
    left: -9999em;
  `}

  ${({ isNavItemActive }) => {
    if (!isNavItemActive) {
      return css`
        ${maxBreakpointQuery.mlarge`
          display: none;
        `}
      `;
    }
  }}
`;

const StyledItem = styled.li`
  ${maxBreakpointQuery.mlarge`
    border-top: 1px solid ${standardColours.transparentWhite(0.1)};

    span {
      color: ${brandColours.primary};
    }
  `}

  ${minBreakpointQuery.mlarge`
    display: inline-block;
    margin-right: 1px;
    margin-left: 1px;
  `}

  ${minBreakpointQuery.large`
    margin-right: 2px;
    margin-left: 2px;
  `}

  ${minBreakpointQuery.xxlarge`
    margin-right: 3px;
    margin-left: 3px;
  `}

  ${minBreakpointQuery.xxxlarge`
    margin-right: 4px;
    margin-left: 4px;
  `}

  ${minBreakpointQuery.xxxxlarge`
    margin-right: 5px;
    margin-left: 5px;
  `}

  &:hover {
    ${StyledLink} {
      ${minBreakpointQuery.mlarge`
        color: ${standardColours.white};
        background-color: ${brandColours.tertiary};
      `}

      &:after {
        ${minBreakpointQuery.mlarge`
          border-color: ${standardColours.white};
        `}
      }
    }

    ${StyledMegaMenu} {
      ${minBreakpointQuery.mlarge`
        right: 0;
        left: 0;
      `}

      ${minBreakpointQuery.large`
        right: 40px;
        left: 40px;
      `}

      ${minBreakpointQuery.xxlarge`
        right: 80px;
        left: 80px;
      `}
    }
  }

  ${({ isNavItemActive }) => {
    if (isNavItemActive) {
      return css`
        ${StyledLink} {
          &:after {
            ${maxBreakpointQuery.mlarge`
              transform: translateY(-50%) rotate(-135deg);
            `}
          }
        }
      `;
    }
  }}
`;

const Item = ({
  url,
  text,
  subMenus,
  callToActions,
  alt,
  altTheme,
  setNavActive,
}) => {
  const [isNavItemActive, setNavItemActive] = useState(false);

  const hasSubMenus = subMenus && subMenus.length > 0;

  return (
    <StyledItem isNavItemActive={isNavItemActive}>
      <StyledLink
        to={url}
        onClick={e => {
          if (
            matchMedia(`(max-width: ${breakpointSizes.mlarge - 1}px)`).matches
          ) {
            if (hasSubMenus) {
              e.preventDefault();
              setNavItemActive(!isNavItemActive);
            } else if (url === window.location.pathname) {
              setNavActive(false);
            }
          }
        }}
        alt={alt}
        altTheme={altTheme}
        activeClassName="current-page"
      >
        <span>{text}</span>
      </StyledLink>
      <StyledMegaMenu
        items={subMenus}
        viewAllLink={
          hasSubMenus && {
            text,
            url,
          }
        }
        callToActions={callToActions}
        setNavActive={setNavActive}
        isNavItemActive={isNavItemActive}
        isVariant={true}
      />
    </StyledItem>
  );
};

const Navigation = ({ alt, altTheme, isNavActive, setNavActive, ...props }) => {
  const {
    spaseekers: {
      navigation: { items },
    },
  } = useStaticQuery(graphql`
    query NavigationVariantQuery {
      spaseekers {
        navigation {
          items: headerVariant {
            text
            url
            subMenus {
              text
              subItems: items {
                text
                url
              }
            }
            callToActions: adornment {
              banners {
                items: banners {
                  heading
                  image {
                    ...ImageFragment
                  }
                  label
                  text
                  url
                }
              }
              spaSpotlight {
                header {
                  heading
                }
                spa {
                  name
                  images {
                    ...ImageFragment
                  }
                  city
                  county {
                    name
                  }
                  minPrice
                  isPricePerPerson
                  url
                }
              }
            }
          }
        }
      }
    }
  `);

  return (
    <StyledNavigation {...props}>
      <StyledToggle
        aria-label="Open / Close Navigation"
        isNavActive={isNavActive}
        onClick={() => setNavActive(!isNavActive)}
      >
        <span />
        <span />
        <span />
      </StyledToggle>
      <StyledList isNavActive={isNavActive}>
        {items.map((item, id) => (
          <Item
            key={id}
            {...item}
            alt={alt}
            altTheme={altTheme}
            setNavActive={setNavActive}
          />
        ))}
      </StyledList>
    </StyledNavigation>
  );
};

export default Navigation;
